const gitCheatsheet = {
  id: "git",
  title: "Git Cheatsheet",
  description:
    "A comprehensive guide to Git version control system including basic commands, branching, merging, remote operations, and advanced workflows",
  language: "bash",
  category: "Version Control",
  level: "All Levels",
  sections: [
    {
      title: "Getting Started",
      items: [
        {
          title: "Installation & Setup",
          content: "Initial Git setup commands",
          code: `# Install Git
# Windows: Download from https://git-scm.com/download/win
# Mac: brew install git
# Linux: sudo apt-get install git

# Configure Git
git config --global user.name "Your Name"
git config --global user.email "your.email@example.com"

# Initialize repository
git init

# Clone repository
git clone https://github.com/username/repository.git
git clone https://github.com/username/repository.git custom-folder

# View configuration
git config --list
git config user.name`,
          note: "Basic setup and configuration commands for Git",
        },
        {
          title: "Basic Commands",
          content: "Essential Git commands for daily use",
          code: `# Check status
git status

# Stage changes
git add filename.txt        # Stage specific file
git add .                  # Stage all changes
git add *.js              # Stage all JavaScript files
git add -p                # Interactive staging

# Commit changes
git commit -m "Commit message"
git commit -am "Add and commit message"  # Stage tracked files and commit
git commit --amend        # Modify last commit

# View history
git log                   # Full history
git log --oneline        # Compact history
git log --graph          # Graphical history
git log -p               # Show patches
git log --stat           # Show stats
git shortlog             # Group by author`,
          note: "Common commands used in everyday Git workflows",
        },
      ],
    },
    {
      title: "Working with Changes",
      items: [
        {
          title: "Inspecting Changes",
          content: "Commands to review changes",
          code: `# View changes
git diff                  # Unstaged changes
git diff --staged        # Staged changes
git diff HEAD            # All changes
git diff commit1 commit2 # Between commits
git diff branch1..branch2 # Between branches

# View file history
git blame filename.txt    # Line-by-line history
git show commit-hash     # Show specific commit
git show HEAD            # Show last commit
git show HEAD~1          # Show second-last commit

# List tracked files
git ls-files             # Show tracked files
git ls-files --others    # Show untracked files`,
          note: "Commands for reviewing and inspecting changes in your repository",
        },
        {
          title: "Undoing Changes",
          content: "Commands to undo changes at different stages",
          code: `# Unstage changes
git restore --staged filename.txt  # Modern syntax
git reset HEAD filename.txt        # Traditional syntax

# Discard changes
git restore filename.txt           # Modern syntax
git checkout -- filename.txt       # Traditional syntax

# Reset commits
git reset --soft HEAD~1   # Undo commit, keep changes staged
git reset --mixed HEAD~1  # Undo commit, unstage changes
git reset --hard HEAD~1   # Undo commit, discard changes

# Revert commits
git revert commit-hash    # Create new commit that undoes changes
git revert HEAD          # Revert last commit

# Clean untracked files
git clean -n             # Dry run
git clean -f             # Force clean
git clean -fd            # Clean directories too
git clean -fx            # Clean ignored files`,
          note: "Different ways to undo changes in Git",
        },
      ],
    },
    {
      title: "Branching and Merging",
      items: [
        {
          title: "Branch Management",
          content: "Working with branches",
          code: `# List branches
git branch               # Local branches
git branch -r            # Remote branches
git branch -a            # All branches
git branch -v            # Verbose (last commit)

# Create branches
git branch branch-name   # Create branch
git checkout -b branch-name # Create and switch
git switch -c branch-name  # Modern syntax

# Switch branches
git checkout branch-name # Traditional syntax
git switch branch-name   # Modern syntax

# Delete branches
git branch -d branch-name  # Safe delete
git branch -D branch-name  # Force delete
git push origin --delete branch-name # Delete remote

# Rename branch
git branch -m old-name new-name
git branch -M old-name new-name  # Force rename`,
          note: "Commands for managing Git branches",
        },
        {
          title: "Merging and Rebasing",
          content: "Combining changes from different branches",
          code: `# Merge branches
git merge branch-name    # Merge into current branch
git merge --no-ff branch-name # Create merge commit
git merge --abort        # Abort merge

# Rebase
git rebase main         # Rebase current branch onto main
git rebase -i HEAD~3    # Interactive rebase
git rebase --abort      # Abort rebase
git rebase --continue   # Continue after resolving conflicts

# Cherry-pick
git cherry-pick commit-hash  # Apply specific commit
git cherry-pick -x commit-hash # Include source
git cherry-pick --abort     # Abort cherry-pick

# Resolve conflicts
git mergetool            # Launch merge tool
git checkout --ours filename   # Keep our version
git checkout --theirs filename # Keep their version`,
          note: "Different strategies for combining changes between branches",
        },
      ],
    },
    {
      title: "Remote Operations",
      items: [
        {
          title: "Remote Management",
          content: "Working with remote repositories",
          code: `# Manage remotes
git remote -v            # List remotes
git remote add origin url # Add remote
git remote rename old new # Rename remote
git remote remove name   # Remove remote
git remote set-url name url # Update URL

# Fetch changes
git fetch origin         # Fetch from origin
git fetch --all         # Fetch all remotes
git fetch --prune       # Remove deleted refs

# Push changes
git push origin branch   # Push to specific branch
git push -u origin branch # Set upstream
git push --force        # Force push (careful!)
git push --force-with-lease # Safer force push

# Pull changes
git pull origin branch   # Fetch and merge
git pull --rebase       # Fetch and rebase
git pull --ff-only      # Fast-forward only`,
          note: "Commands for working with remote repositories",
        },
        {
          title: "Collaboration",
          content: "Working with others",
          code: `# Fork workflow
git remote add upstream original-repo-url
git fetch upstream
git merge upstream/main

# Create pull request (GitHub)
git push origin feature-branch
# Then create PR on GitHub

# Update pull request
git push origin feature-branch --force-with-lease

# Review others' PR locally
git fetch origin pull/ID/head:pr-branch
git checkout pr-branch`,
          note: "Commands for collaborative Git workflows",
        },
      ],
    },
    {
      title: "Advanced Features",
      items: [
        {
          title: "Stashing",
          content: "Temporarily storing changes",
          code: `# Basic stash operations
git stash               # Stash changes
git stash save "message" # Stash with message
git stash list          # List stashes
git stash show          # Show stash contents
git stash pop           # Apply and remove stash
git stash apply         # Apply but keep stash
git stash drop          # Remove stash
git stash clear         # Remove all stashes

# Selective stashing
git stash -p            # Interactive stash
git stash push -m "message" file.txt # Stash specific file`,
          note: "Commands for managing the stash",
        },
        {
          title: "Submodules and Subtrees",
          content: "Working with project dependencies",
          code: `# Submodules
git submodule add url path  # Add submodule
git submodule init         # Initialize submodules
git submodule update      # Update submodules
git submodule update --init --recursive # Init & update

# Subtrees
git subtree add --prefix=path url branch # Add subtree
git subtree pull --prefix=path url branch # Update
git subtree push --prefix=path url branch # Push changes`,
          note: "Managing project dependencies with Git",
        },
        {
          title: "Advanced Tools",
          content: "Additional Git features and tools",
          code: `# Bisect - Find bugs
git bisect start
git bisect bad          # Current version is bad
git bisect good v1.0    # v1.0 was good
git bisect reset        # End bisect

# Worktree - Multiple working copies
git worktree add ../path branch
git worktree list
git worktree remove ../path

# Reflog - Recovery
git reflog              # View reference logs
git reset --hard HEAD@{1} # Restore previous state

# Archive
git archive --format=zip HEAD > project.zip
git archive --format=tar.gz main > project.tar.gz`,
          note: "Advanced Git tools and features",
        },
      ],
    },
    {
      title: "Best Practices",
      items: [
        {
          title: "Commit Messages",
          content: "Writing good commit messages",
          code: `# Commit message format
<type>(<scope>): <subject>

# Types:
feat: New feature
fix: Bug fix
docs: Documentation
style: Formatting
refactor: Code restructuring
test: Adding tests
chore: Maintenance

# Example
feat(auth): add OAuth2 authentication

# Detailed message
git commit -m "feat(auth): add OAuth2 authentication" -m "- Add Google OAuth provider
- Implement token refresh
- Add user profile sync"`,
          note: "Guidelines for writing clear and useful commit messages",
        },
        {
          title: "Gitignore",
          content: "Managing ignored files",
          code: `# Common .gitignore patterns
# Node.js
node_modules/
npm-debug.log

# Build outputs
dist/
build/
*.min.js

# IDE files
.vscode/
.idea/
*.swp

# OS files
.DS_Store
Thumbs.db

# Logs and databases
*.log
*.sqlite

# Environment variables
.env
.env.local`,
          note: "Common patterns for Git ignore files",
        },
      ],
    },
  ],
};

export default gitCheatsheet;
